import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";
import FoxLogo from "@/components/FoxLogo";

export default function Privacy() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <header className="border-b border-stone-200 bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl tracking-tight inline-flex items-center gap-2" data-testid="brand-logo">
            <FoxLogo size={32} />Xenia<span className="text-blue-700">.</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Link to="/login" className="text-sm text-stone-700 hover:text-stone-900">{t("nav.login")}</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16" data-testid="privacy-content">
        <p className="text-xs tracked text-blue-700 mb-3">GDPR · REG. UE 2016/679 · D.LGS. 196/2003 (CODICE PRIVACY)</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2">{t("privacy.privacy_title")}</h1>
        <p className="text-stone-600 mb-2">
          Versione 2.0 · Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
        </p>
        <p className="text-xs text-stone-500 italic mb-12 p-3 bg-amber-50 border border-amber-200">
          ⚠️ {t("privacy.legal_disclaimer")}
        </p>

        {/* Indice */}
        <nav className="mb-12 p-5 bg-white border border-stone-200 text-sm">
          <p className="text-xs tracked text-stone-500 mb-3">INDICE</p>
          <ol className="space-y-1 list-decimal pl-5 text-stone-700">
            <li><a href="#titolare" className="hover:text-blue-700">Titolare e Responsabile</a></li>
            <li><a href="#dati" className="hover:text-blue-700">Dati trattati</a></li>
            <li><a href="#finalita" className="hover:text-blue-700">Finalità e base giuridica</a></li>
            <li><a href="#tempi" className="hover:text-blue-700">Tempi di conservazione</a></li>
            <li><a href="#destinatari" className="hover:text-blue-700">Destinatari e sub-processor</a></li>
            <li><a href="#trasferimento" className="hover:text-blue-700">Trasferimento extra-UE</a></li>
            <li><a href="#diritti" className="hover:text-blue-700">Diritti dell'interessato</a></li>
            <li><a href="#sicurezza" className="hover:text-blue-700">Sicurezza dei dati</a></li>
            <li><a href="#minori" className="hover:text-blue-700">Minori</a></li>
            <li><a href="#decisioni" className="hover:text-blue-700">Decisioni automatizzate</a></li>
            <li><a href="#cookie" className="hover:text-blue-700">Cookie</a></li>
            <li><a href="#modifiche" className="hover:text-blue-700">Modifiche</a></li>
            <li><a href="#contatti" className="hover:text-blue-700">Contatti e DPO</a></li>
          </ol>
        </nav>

        <Section id="titolare" title="1. Titolare e Responsabile del trattamento">
          <p><strong>Titolare del trattamento</strong> dei dati personali degli ospiti registrati nel sistema è il proprietario dell'account Xenia (gestore della struttura ricettiva), che agisce in autonomia per gli adempimenti previsti dalla legge italiana (TULPS art. 109, normativa comunale tassa di soggiorno, normativa fiscale).</p>
          <p className="mt-3">Xenia (di seguito anche "il Servizio") opera in qualità di <strong>Responsabile esterno del trattamento</strong> ai sensi dell'art. 28 GDPR, fornendo l'infrastruttura software per il trattamento dei dati per conto del Titolare. È stipulato un Data Processing Agreement (DPA) implicito al momento dell'accettazione dei Termini di Servizio.</p>
          <p className="mt-3">Per i dati dell'utente registrato (gestore), Xenia è invece <strong>Titolare autonomo</strong>.</p>
        </Section>

        <Section id="dati" title="2. Tipologie di dati trattati">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Dati account utente</strong>: nome e cognome, email, password (cifrata con bcrypt), telefono opzionale, lingua preferita.</li>
            <li><strong>Dati delle strutture ricettive</strong>: ragione sociale, indirizzo, comune, P.IVA, codice SDI, dati fiscali.</li>
            <li><strong>Dati anagrafici degli ospiti</strong> (Pubblica Sicurezza ex art. 109 TULPS — categoria di dati con base obbligatoria per legge):
              <ul className="list-disc pl-5 mt-1 text-stone-600">
                <li>nome, cognome, sesso, data e luogo di nascita</li>
                <li>cittadinanza</li>
                <li>tipo, numero, data e luogo di rilascio del documento d'identità</li>
                <li>autorità rilasciante</li>
                <li>data e luogo di provenienza per cittadini stranieri</li>
              </ul>
            </li>
            <li><strong>Dati di soggiorno</strong>: date di check-in/check-out, camera assegnata, prezzo, occupanti, esenzioni tassa di soggiorno e relative motivazioni.</li>
            <li><strong>Dati di pagamento</strong>: metodo (contanti/carta/bonifico/OTA), stato, importi pagati. <em>Nessun dato di carta di credito è memorizzato dal Servizio</em> — i pagamenti sono gestiti via Stripe (PCI-DSS Level 1 certified) che riceve direttamente i dati di pagamento.</li>
            <li><strong>Dati di contatto delle aziende clienti</strong>: ragione sociale, P.IVA, PEC, codice destinatario SDI, indirizzo.</li>
            <li><strong>Dati tecnici</strong>: indirizzo IP, user-agent, log di accesso, cookie tecnici di sessione, eventi di sicurezza.</li>
            <li><strong>Documenti scansionati</strong>: durante la scansione AI, l'immagine del documento viene <strong>trasmessa al fornitore di OCR (Google Gemini via Emergent)</strong> per l'estrazione testo. L'immagine <strong>non viene memorizzata sui nostri server</strong> oltre il tempo dell'elaborazione (max 30 secondi).</li>
          </ul>
        </Section>

        <Section id="finalita" title="3. Finalità del trattamento e base giuridica">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Esecuzione del contratto</strong> (<em>art. 6.1.b GDPR</em>): erogazione del software, gestione account, fatturazione abbonamento.</li>
            <li><strong>Obbligo legale</strong> (<em>art. 6.1.c GDPR</em>):
              <ul className="list-disc pl-5 mt-1 text-stone-600">
                <li>comunicazione alle Questure delle generalità degli alloggiati (TULPS art. 109)</li>
                <li>calcolo e versamento della tassa di soggiorno al Comune</li>
                <li>obblighi fiscali (DPR 633/72, art. 22 TUIR, art. 2220 c.c.)</li>
                <li>antiriciclaggio per pagamenti di importo elevato (D.Lgs. 231/2007)</li>
              </ul>
            </li>
            <li><strong>Legittimo interesse</strong> (<em>art. 6.1.f GDPR</em>): sicurezza dell'infrastruttura, prevenzione frodi, miglioramento del servizio. Bilanciamento documentato disponibile su richiesta.</li>
            <li><strong>Consenso</strong> (<em>art. 6.1.a GDPR</em>): invio di email/SMS di conferma prenotazione all'ospite (consenso del gestore per conto dell'ospite, basato sull'interesse legittimo dell'ospite a ricevere conferma); newsletter e marketing (sempre revocabile).</li>
          </ul>
        </Section>

        <Section id="tempi" title="4. Tempi di conservazione">
          <p className="mb-4 text-stone-700">I dati sono conservati per il tempo strettamente necessario alle finalità per cui sono stati raccolti e in conformità agli obblighi di legge:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-stone-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-3 border-b border-stone-200 font-medium">Tipologia di dato</th>
                  <th className="text-left p-3 border-b border-stone-200 font-medium">Periodo</th>
                  <th className="text-left p-3 border-b border-stone-200 font-medium">Base normativa</th>
                </tr>
              </thead>
              <tbody className="text-stone-700">
                <Row d="Schedine alloggiati (TULPS)" t="5 anni dalla data di partenza" b="Art. 109 TULPS · Provv. Garante 26/4/2010" />
                <Row d="Documentazione fiscale (fatture, registri tassa di soggiorno)" t="10 anni dalla chiusura dell'esercizio" b="Art. 2220 c.c. · DPR 633/72" />
                <Row d="Dati account utente (gestore)" t="Durata del contratto + 30 giorni dalla chiusura" b="Art. 6.1.b GDPR" />
                <Row d="Dati account utente — backup" t="90 giorni dalla cancellazione" b="Recupero accidentale" />
                <Row d="Pagamenti Stripe" t="10 anni" b="Antiriciclaggio + obblighi fiscali" />
                <Row d="Dati ospiti (oltre alloggiati)" t="2 anni dalla partenza" b="Esercizio diritti civili residuali" />
                <Row d="Email / SMS log mittenza" t="12 mesi" b="Legittimo interesse · prova di invio" />
                <Row d="Log di accesso utente (audit)" t="6 mesi" b="Provv. Garante 27/11/2008" />
                <Row d="Log applicativi tecnici" t="30 giorni" b="Sicurezza · diagnostica" />
                <Row d="Indirizzi IP" t="6 mesi" b="Sicurezza · prevenzione frodi" />
                <Row d="Cookie tecnici di sessione" t="Sessione (max 30 giorni)" b="Necessari" />
                <Row d="Cookie analitici" t="Non utilizzati" b="—" />
                <Row d="Marketing / newsletter (consenso)" t="Fino a revoca o 24 mesi inattività" b="Art. 6.1.a GDPR" />
                <Row d="Documenti scansionati (immagine)" t="Non memorizzati (cancellati post-elaborazione)" b="Minimizzazione · art. 5 GDPR" />
                <Row d="Storico prenotazioni e occupanti" t="10 anni" b="Obblighi fiscali · contabili" />
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-stone-600">
            Trascorsi i termini, i dati vengono cancellati o resi anonimi. Eventuali richieste di cancellazione anticipata
            (art. 17 GDPR) saranno valutate caso per caso, fatti salvi gli obblighi di legge.
          </p>
        </Section>

        <Section id="destinatari" title="5. Destinatari dei dati e sub-responsabili">
          <p className="mb-3">I dati sono trattati internamente da personale autorizzato del Titolare e possono essere comunicati a:</p>
          <ul className="list-disc pl-5 space-y-1 mb-4">
            <li><strong>Autorità di Pubblica Sicurezza</strong>: invio obbligatorio schedine alloggiati alle Questure (Polizia di Stato).</li>
            <li><strong>Comune di ubicazione della struttura</strong>: dichiarazione tassa di soggiorno.</li>
            <li><strong>Agenzia delle Entrate</strong>: fatturazione elettronica, dichiarazioni fiscali.</li>
            <li><strong>Consulenti contabili / commercialisti</strong> incaricati dal Titolare.</li>
          </ul>

          <p className="mb-2 text-sm text-stone-700">I seguenti sub-responsabili (art. 28 GDPR) trattano i dati per conto del Titolare:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-stone-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-2 border-b border-stone-200">Fornitore</th>
                  <th className="text-left p-2 border-b border-stone-200">Servizio</th>
                  <th className="text-left p-2 border-b border-stone-200">Sede / DPA</th>
                </tr>
              </thead>
              <tbody className="text-stone-700">
                <SubProc name="Emergent" service="Hosting & infrastruttura cloud" loc="USA · SCC + DPF" />
                <SubProc name="MongoDB Atlas" service="Database" loc="UE (Frankfurt)" />
                <SubProc name="Stripe" service="Pagamenti carta · PCI-DSS L1" loc="Irlanda · DPF" />
                <SubProc name="Google (Gemini API)" service="OCR documenti via Emergent" loc="USA · DPF" />
                <SubProc name="Resend" service="Invio email transazionali" loc="USA · DPF" />
                <SubProc name="Twilio" service="Invio SMS transazionali" loc="USA · DPF" />
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-stone-500">
            DPF = EU-US Data Privacy Framework. SCC = Standard Contractual Clauses (Decisione 2021/914 UE).
            La lista aggiornata è disponibile su richiesta scrivendo a privacy@xeniaregistry.com.
          </p>
        </Section>

        <Section id="trasferimento" title="6. Trasferimento dei dati extra-UE">
          <p>Alcuni sub-responsabili sopra elencati hanno sede o trattano dati in paesi extra-UE (principalmente Stati Uniti). Tali trasferimenti avvengono con garanzie adeguate:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>EU-US Data Privacy Framework</strong> (Decisione di adeguatezza UE 10/7/2023) per i fornitori certificati.</li>
            <li><strong>Standard Contractual Clauses</strong> (SCC) per gli altri trasferimenti.</li>
            <li>Misure tecniche supplementari: cifratura in transito (TLS 1.3) e a riposo (AES-256), pseudonimizzazione dei dati ove possibile.</li>
          </ul>
        </Section>

        <Section id="diritti" title="7. Diritti dell'interessato">
          <p className="mb-3">In qualsiasi momento puoi esercitare i seguenti diritti previsti dagli artt. 15-22 GDPR:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Accesso</strong> (art. 15) — sapere quali tuoi dati sono trattati</li>
            <li><strong>Rettifica</strong> (art. 16) — correzione di dati inesatti</li>
            <li><strong>Cancellazione / oblio</strong> (art. 17) — fatti salvi gli obblighi di legge</li>
            <li><strong>Limitazione</strong> (art. 18)</li>
            <li><strong>Portabilità</strong> (art. 20) — ricevere i dati in formato JSON/CSV</li>
            <li><strong>Opposizione</strong> (art. 21)</li>
            <li><strong>Revoca del consenso</strong> in qualsiasi momento (art. 7.3)</li>
            <li><strong>Reclamo all'Autorità di Controllo</strong>: <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-blue-700 underline">Garante per la protezione dei dati personali</a> (Italia) o autorità del proprio Stato membro UE</li>
          </ul>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 text-sm">
            <p className="font-medium mb-1">Come esercitare i diritti?</p>
            <p>Dall'app: <strong>Impostazioni → I miei dati</strong>. Oppure via email a <strong>privacy@xeniaregistry.com</strong>. Risposta entro <strong>30 giorni</strong> (art. 12.3).</p>
          </div>
        </Section>

        <Section id="sicurezza" title="8. Misure di sicurezza">
          <ul className="list-disc pl-5 space-y-1">
            <li>Cifratura TLS 1.3 per tutte le comunicazioni</li>
            <li>Password degli utenti cifrate con <strong>bcrypt</strong> (cost 12)</li>
            <li>Sessioni autenticate via <strong>JWT firmati</strong> con segreto a 256 bit</li>
            <li>Autenticazione a due fattori opzionale tramite Google OAuth</li>
            <li>Database con accesso ristretto via VPC, backup giornalieri cifrati, ritenzione 90 giorni</li>
            <li>Log di accesso, monitoraggio anomalie, rate limiting</li>
            <li>Test di penetrazione e audit di sicurezza periodici</li>
            <li>Procedura di Data Breach Notification entro 72h al Garante (art. 33 GDPR)</li>
          </ul>
        </Section>

        <Section id="minori" title="9. Trattamento di dati di minori">
          <p>Il Servizio non è destinato a minori di 16 anni. I dati anagrafici dei minori che soggiornano in struttura sono trattati dal Titolare esclusivamente per gli adempimenti di Pubblica Sicurezza (TULPS art. 109) e tassa di soggiorno (esenzioni minori). Non vengono usati per profilazione né marketing.</p>
        </Section>

        <Section id="decisioni" title="10. Decisioni automatizzate e profilazione">
          <p>Il Servizio utilizza AI esclusivamente per <strong>estrarre il testo</strong> dai documenti d'identità scansionati (OCR via Gemini). Non vengono prese decisioni automatizzate che producano effetti giuridici sull'interessato (art. 22 GDPR).</p>
          <p className="mt-2">Il calcolo della tassa di soggiorno è automatico ma deterministico (in base a regolamento comunale), non probabilistico, e l'utente può sempre verificarne e correggerne l'esito.</p>
        </Section>

        <Section id="cookie" title="11. Cookie">
          <p>Utilizziamo esclusivamente <strong>cookie tecnici</strong> necessari al funzionamento del Servizio (sessione utente, preferenze lingua). Non utilizziamo cookie di profilazione o pubblicitari di terze parti. Maggiori dettagli nella <Link to="/cookie-policy" className="text-blue-700 underline">Cookie Policy</Link>.</p>
        </Section>

        <Section id="modifiche" title="12. Modifiche all'informativa">
          <p>Ci riserviamo il diritto di aggiornare questa informativa. Modifiche sostanziali saranno notificate via email o tramite avviso nell'app almeno <strong>30 giorni prima</strong> dell'entrata in vigore. La versione corrente è sempre disponibile a questo URL.</p>
        </Section>

        <Section id="contatti" title="13. Contatti e Data Protection Officer">
          <ul className="list-none space-y-2">
            <li><strong>Email Privacy</strong>: privacy@xeniaregistry.com</li>
            <li><strong>DPO</strong> (Responsabile della Protezione dei Dati): <em>nominato e contattabile</em> all'indirizzo dpo@xeniaregistry.com (per richieste degli interessati e segnalazioni)</li>
            <li><strong>Sede legale del Titolare</strong>: indicata nell'account utente / disponibile su richiesta</li>
          </ul>
          <p className="mt-3 text-sm text-stone-500">
            Per gli ospiti delle strutture: rivolgiti direttamente al gestore della struttura presso cui hai soggiornato.
            Per quesiti sulla piattaforma: <strong>privacy@xeniaregistry.com</strong>.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-stone-200 flex flex-wrap gap-4">
          <Link to="/signup" className="bg-stone-900 text-stone-50 px-5 py-3 text-sm rounded-sm hover:bg-stone-800">
            Torna alla registrazione
          </Link>
          <Link to="/cookie-policy" className="border border-stone-300 px-5 py-3 text-sm rounded-sm hover:bg-stone-50">
            Cookie Policy
          </Link>
          <Link to="/" className="text-sm text-stone-700 underline underline-offset-4 self-center">
            Vai alla homepage
          </Link>
        </div>
      </main>

      <footer className="border-t border-stone-200 py-8 text-center text-xs text-stone-500">
        © {new Date().getFullYear()} Xenia · The Global Guest Registry · GDPR-compliant
      </footer>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-20">
      <h2 className="font-serif text-2xl tracking-tight mb-3">{title}</h2>
      <div className="text-stone-700 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

function Row({ d, t, b }) {
  return (
    <tr className="border-b border-stone-100">
      <td className="p-3 align-top">{d}</td>
      <td className="p-3 align-top font-medium">{t}</td>
      <td className="p-3 align-top text-stone-500">{b}</td>
    </tr>
  );
}

function SubProc({ name, service, loc }) {
  return (
    <tr className="border-b border-stone-100">
      <td className="p-2 align-top font-medium">{name}</td>
      <td className="p-2 align-top">{service}</td>
      <td className="p-2 align-top text-stone-500">{loc}</td>
    </tr>
  );
}
